import React from 'react';
import { Users, User, List, FileText, BarChart3, LogOut, Menu, MessageSquare } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Button } from './ui/button';
import { Card } from './ui/card';

interface LayoutProps {
  children: React.ReactNode;
  currentPage: 'candidates' | 'evaluator' | 'evaluations' | 'admin' | 'discussion';
  onPageChange: (page: 'candidates' | 'evaluator' | 'evaluations' | 'admin' | 'discussion') => void;
  userType: 'candidate' | 'evaluator' | 'admin' | null;
  onLogout: () => void;
  isAdmin?: boolean;
}

export default function Layout({ 
  children, 
  currentPage, 
  onPageChange, 
  userType,
  onLogout,
  isAdmin = false
}: LayoutProps) {
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    onLogout();
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <nav className="border-b bg-card/50 backdrop-blur supports-[backdrop-filter]:bg-card/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <FileText className="h-8 w-8 text-primary mr-3" />
              <h1 className="text-xl font-bold text-foreground hidden sm:block">{currentPage === 'admin' ? '管理者モード' : '通常モード'}</h1>
              <h1 className="text-lg font-bold text-foreground sm:hidden">{currentPage === 'admin' ? '管理者モード' : '通常モード'}</h1>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* マイページ: 管理者でも常に表示。admin の場合は evaluator ページに誘導 */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPageChange(userType === 'candidate' ? 'candidates' : 'evaluator')}
                className={
                  (userType === 'candidate' && currentPage === 'candidates') ||
                  (userType !== 'candidate' && currentPage === 'evaluator')
                    ? 'bg-accent text-accent-foreground'
                    : ''
                }
              >
                <User className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">マイページ</span>
              </Button>
              {/* 発表一覧: 常に表示 */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPageChange('evaluations')}
                className={
                  currentPage === 'evaluations'
                    ? 'bg-accent text-accent-foreground'
                    : ''
                }
              >
                <List className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">発表一覧</span>
              </Button>
              
              {/* 管理者モードでも上部の並びは固定（マイページ・発表一覧・ディスカッション） */}
              
              {/* Discussion button for all users */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPageChange('discussion')}
                className={
                  currentPage === 'discussion'
                    ? 'bg-accent text-accent-foreground'
                    : ''
                }
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">ディスカッション</span>
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignOut}
              >
                <LogOut className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">ログアウト</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>
      
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 space-y-6 flex-1">
        {children}
      </main>

      <footer className="border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <p className="text-right text-xs text-muted-foreground">© 2025 amiac one</p>
        </div>
      </footer>
    </div>
  );
}
